/**
 * The Navigator — say what you want to do, and it finds the place or the
 * starting point (PRD-navigator-that-does-it FR-0, FR-1).
 *
 * One Navigator, no choice at the door: it answers in the workspace's voice
 * (plain, Harry or Wes, set in Settings → Agents). It never opens blank — it
 * starts from what needs the person now (the engine's REQUIRED cards, the same
 * list Home and My work draw) and the jobs worth doing on this page. What it
 * finds comes from the one catalog (`lib/navigator/catalog`), matched by rules
 * first; Harry and Wes may add entries by id and a sentence in their voice.
 * Use is recorded as counts and entry ids, never the words typed.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  X, ArrowRight, Star, CornerDownLeft, Trash2, Loader2, Quote,
} from 'lucide-react';
import {
  CATALOG,
  getEntry,
  jobsForRoute,
  matchCatalog,
  type CatalogEntry,
  type CatalogMatch,
  type JobEntry,
  type StartEntry,
} from '../../lib/navigator/catalog';
import { launchStartPoint } from '../../apps/start/launchStartPoint';
import { useConciergeStore, type ConciergeEntry } from '../../stores/prioritization/conciergeStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useHarryStore } from '../../stores/prioritization/harryStore';
import { useWesStore } from '../../stores/prioritization/wesStore';
import { askHarry, AgentLlmError } from '../../lib/llmHarryService';
import { askWes } from '../../lib/llmWesService';
import { buildHarryUserPrompt } from '../../lib/harry/harryPrompts';
import { buildWesUserPrompt } from '../../lib/wes/wesPrompts';
import { getGuideAgent } from '../../lib/guide/guideAgents';
import { harryPageContext } from '../../lib/harry/harryPageContext';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { useModuleGate } from '../../lib/moduleGate';
import { getStoredUiMode } from '../../lib/uiMode';
import { buildSurfacePacket, type SurfacePacket } from '../../lib/ai/contextPacket';
import { retrieveCanonSections } from '../../lib/ai/canonRetrieval';
import { isAiCapabilityEnabled } from '../../lib/ai/aiAssistSettings';
import { recordAppEvent, routeFamily } from '../../lib/appTelemetry';
import { getResponsibilityNavigationHref } from '../../lib/responsibilityLinks';
import { useNeedsYouCards } from '../../hooks/useNeedsYouCount';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useHarryIsmStore, type HarryIsmEntry } from '../../stores/prioritization/harryIsmStore';
import { HarryWheelIcon } from './HarryWheelIcon';
import { HarryIsmCard } from './HarryIsmCard';
import { useHarryIsmSpin } from './useHarryIsmSpin';
import { useHarryShell } from './harryShellContext';
import { AssistantAvatar } from './AssistantAvatar';

/** How many of the person's open asks the Navigator opens on. */
const NEEDS_YOU_SHOWN = 3;
/** How many recent questions it keeps in view. */
const RECENT_SHOWN = 6;

const sectionLabel = 'mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)]';
const rowButton =
  'flex w-full items-center gap-2.5 border px-3 py-2 text-left transition-colors border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/40 dark:border-[var(--rule-bright)] dark:hover:border-[rgba(61,75,111,0.45)] dark:hover:bg-[var(--active-tint)]';

function StarButton({ query, entryId }: { query: string; entryId: string }) {
  const isStarred = useConciergeStore((s) => s.isStarred);
  const toggleStar = useConciergeStore((s) => s.toggleStar);
  const userId = useUserStore((s) => s.userId);
  const starred = isStarred(query);
  return (
    <button
      type="button"
      onClick={() => toggleStar(userId, { id: '', query, capabilityId: entryId, ts: Date.now() })}
      className={`ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
        starred
          ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[rgba(212,160,23,0.15)]'
          : 'text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)]'
      }`}
      title={starred ? 'Unpin from Recent' : 'Keep this at the top of Recent'}
    >
      <Star className={`h-3 w-3 ${starred ? 'fill-current' : ''}`} />
      {starred ? 'Pinned' : 'Pin'}
    </button>
  );
}

function JobCard({
  job,
  query,
  showHowTo,
  onGo,
}: {
  job: JobEntry;
  query: string | null;
  showHowTo: boolean;
  onGo: (href: string) => void;
}) {
  const Icon = job.icon;
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 dark:border-[var(--rule-bright)]">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--active-tint)] dark:text-[#8EA7D8]">
          <Icon style={{ width: 18, height: 18 }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-[var(--text-pri)]">{job.title}</h4>
            {query && <StarButton query={query} entryId={job.id} />}
          </div>
          <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">{job.summary}</p>
          {showHowTo && job.howTo.length > 0 && (
            <ul className="mt-2 space-y-1 border-l-2 border-[var(--slate)] pl-3 dark:border-[var(--rule-bright)]">
              {job.howTo.map((step, i) => (
                <li key={i} className="text-xs leading-relaxed text-[var(--text-sec)]">{step}</li>
              ))}
            </ul>
          )}
          {job.modalities && job.modalities.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {/* A way that is not built yet is not drawn (build contract §2.8). */}
              {job.modalities.filter((m) => !m.comingSoon && m.href).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => m.href && onGo(m.href)}
                  title={m.description}
                  className="inline-flex items-center gap-1.5 border border-[var(--slate)] bg-[var(--bg-raised)] px-2.5 py-1.5 text-xs font-medium text-[var(--text-ter)] transition-colors hover:bg-[var(--slate-dim)] dark:border-[rgba(61,75,111,0.35)] dark:bg-[var(--surface)] dark:text-[#8EA7D8] dark:hover:bg-[var(--active-tint)]"
                >
                  {m.label}
                  <ArrowRight className="h-3 w-3" />
                </button>
              ))}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onGo(job.href)}
              className="mt-3 inline-flex items-center gap-1.5 bg-[var(--slate)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-90"
            >
              Go there
              <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StartCard({
  start,
  query,
  onStart,
}: {
  start: StartEntry;
  query: string | null;
  onStart: () => void;
}) {
  const Icon = start.icon;
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 dark:border-[var(--rule-bright)]">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--active-tint)] dark:text-[#8EA7D8]">
          <Icon style={{ width: 18, height: 18 }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-[var(--text-pri)]">{start.title}</h4>
            {query && <StarButton query={query} entryId={start.id} />}
          </div>
          <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">{start.summary}</p>
          <p className="mt-1 text-xs text-[var(--text-sec)]">{start.outcomeLine}</p>
          <button
            type="button"
            onClick={onStart}
            className="mt-3 inline-flex items-center gap-1.5 bg-[var(--slate)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-90"
          >
            Start
            <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function HarryPanel({
  onClose,
  initialQuery = null,
}: {
  onClose: () => void;
  /** A sentence already asked elsewhere (⌘K): the Navigator opens on its answer. */
  initialQuery?: string | null;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const can = useUserStore((s) => s.can);
  const workspaceId = getCurrentWorkspaceId();

  const harryConfig = useHarryStore((s) => s.config);
  const harryVoiceMode = useHarryStore((s) => s.voiceMode);
  const wesConfig = useWesStore((s) => s.config);
  const wesVoiceMode = useWesStore((s) => s.voiceMode);
  const { voice, setCornerAssistant } = useHarryShell();
  const voiceMeta = getGuideAgent(voice);

  const llmConfig = voice === 'wes' ? wesConfig : voice === 'harry' ? harryConfig : null;

  const hydrate = useConciergeStore((s) => s.hydrate);
  const record = useConciergeStore((s) => s.record);
  const toggleStar = useConciergeStore((s) => s.toggleStar);
  const clearHistory = useConciergeStore((s) => s.clearHistory);
  const history = useConciergeStore((s) => s.history);
  const starred = useConciergeStore((s) => s.starred);

  const hydrateIsms = useHarryIsmStore((s) => s.hydrate);
  const toggleIsmFavorite = useHarryIsmStore((s) => s.toggleFavorite);
  const isIsmFavorite = useHarryIsmStore((s) => s.isFavorite);
  const removeIsmFavorite = useHarryIsmStore((s) => s.removeFavorite);
  const favoriteIsms = useHarryIsmStore((s) => s.favorites);

  const needsYou = useNeedsYouCards(userId, workspaceId);

  const [input, setInput] = useState(initialQuery ?? '');
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [llmReply, setLlmReply] = useState<string | null>(null);
  const [llmEntryIds, setLlmEntryIds] = useState<string[]>([]);
  /** An entry the person picked from a list: shown first, whatever its title matches. */
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmError, setLlmError] = useState<string | null>(null);

  const { ism, spinning, error: ismError, spin, clearIsm } = useHarryIsmSpin(submitted);

  const target = routeFamily(location.pathname);

  useEffect(() => { hydrate(userId); hydrateIsms(userId); }, [userId, hydrate, hydrateIsms]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const { title, overview } = harryPageContext(location.pathname);
  const onPage = useMemo(() => jobsForRoute(location.pathname), [location.pathname]);

  // Canon-Aware Harry (Slice 1): layered instruction stack + surface packet,
  // behind the canonical module + flag gate (build contract §2.6/§2.7).
  const canonAware = useModuleGate('canon_aware_harry');
  const currentRole = useUserStore((s) => s.currentRole);
  // Per-query packet: canon retrieval is query-dependent (search_canon) and
  // entity enrichment is route-dependent (get_frame_status/get_decision_unit)
  // — all deterministic enrichers from the agent tool registry.
  const buildPacketForQuery = useCallback(async (query: string): Promise<SurfacePacket> => {
    const surface = location.pathname.startsWith('/planning')
      ? 'planning'
      : location.pathname.includes('/sessions') ? 'sessions' : null;

    const sessionId = location.pathname.match(/\/prioritization\/sessions\/([0-9a-f-]{16,})/)?.[1] ?? null;
    const sessionRow = sessionId ? useSessionStore.getState().getSession(sessionId) : undefined;
    const itemId = location.pathname.match(/\/prioritization\/items\/([0-9a-f-]{16,})/)?.[1] ?? null;
    const itemRow = itemId ? useItemsStore.getState().getItemById(itemId) : undefined;

    const canonSections = await retrieveCanonSections(query).catch(() => []);

    return buildSurfacePacket({
      route: location.pathname,
      page: { title, overview },
      uiMode: surface ? getStoredUiMode(surface) : null,
      workspaceId: workspaceId ?? null,
      viewerRole: currentRole ?? null,
      routeCapabilityIds: onPage.map((c) => c.id),
      canonSections,
      session: sessionRow ? { id: sessionRow.id, name: sessionRow.name ?? null, state: sessionRow.state } : null,
      item: itemRow ? { id: itemRow.id, title: itemRow.title ?? null, lifecycleState: itemRow.lifecycle_state ?? null } : null,
    });
  }, [location.pathname, title, overview, workspaceId, currentRole, onPage]);

  const keywordMatches = useMemo(() => (submitted ? matchCatalog(submitted) : []), [submitted]);

  const matches = useMemo(() => {
    if (!submitted) return [] as CatalogMatch[];
    const seen = new Set<string>();
    const out: CatalogMatch[] = [];
    for (const id of [...(pickedId ? [pickedId] : []), ...llmEntryIds]) {
      const entry = getEntry(id);
      if (entry && !seen.has(entry.id)) {
        seen.add(entry.id);
        out.push({ entry, score: 1, matchedTerms: [] });
      }
    }
    for (const m of keywordMatches) {
      if (!seen.has(m.entry.id)) out.push(m);
    }
    return out;
  }, [submitted, pickedId, llmEntryIds, keywordMatches]);

  const go = (href: string, toolId: string) => {
    void recordAppEvent('navigator.went', { appId: 'navigator', toolId, target: routeFamily(href) });
    navigate(href);
    onClose();
  };

  const start = (entry: StartEntry) => {
    void recordAppEvent('navigator.went', { appId: 'navigator', toolId: entry.id, target });
    onClose();
    void launchStartPoint(entry, navigate);
  };

  const runAsk = useCallback(async (q: string) => {
    const query = q.trim();
    if (!query) return;
    const found = matchCatalog(query);
    const top = found[0];
    record(userId, query, top?.entry.id ?? null);
    setSubmitted(query);
    setPickedId(null);
    setLlmReply(null);
    setLlmEntryIds([]);
    setLlmError(null);

    const asked = (state: 'keyword' | 'ai' | 'none', toolId?: string) => {
      void recordAppEvent('navigator.asked', { appId: 'navigator', toolId, state, count: found.length, target });
    };

    if (!voiceMeta.usesLlm) {
      asked(top ? 'keyword' : 'none', top?.entry.id);
      return;
    }

    if (!llmConfig?.enabled || !workspaceId) {
      asked(top ? 'keyword' : 'none', top?.entry.id);
      setLlmError(`${voiceMeta.name} can't answer right now. What's below was found from your words.`);
      return;
    }

    setLlmLoading(true);
    try {
      const userPromptArgs = {
        query,
        pageTitle: title,
        pageOverview: overview,
        routeCapabilities: onPage.map((c) => c.title),
      };
      // Canon-aware: the surface packet carries page/route/canon context, so
      // the user prompt is just the question. Legacy: inline context.
      const packet = canonAware ? await buildPacketForQuery(query) : null;
      const result = voice === 'wes'
        ? await askWes({
            config: llmConfig,
            voiceMode: wesVoiceMode,
            workspaceId,
            userPrompt: canonAware ? query : buildWesUserPrompt(userPromptArgs),
            canonAware,
            packet,
            uiMode: packet?.uiMode ?? null,
          })
        : await askHarry({
            config: llmConfig,
            voiceMode: harryVoiceMode,
            workspaceId,
            userPrompt: canonAware ? query : buildHarryUserPrompt(userPromptArgs),
            canonAware,
            packet,
            uiMode: packet?.uiMode ?? null,
          });
      setLlmReply(result.reply);
      setLlmEntryIds(result.capabilityIds);
      const aiTop = result.capabilityIds.find((id) => getEntry(id));
      if (aiTop) asked('ai', aiTop);
      else asked(top ? 'keyword' : 'none', top?.entry.id);
    } catch (err) {
      asked(top ? 'keyword' : 'none', top?.entry.id);
      const msg = err instanceof AgentLlmError
        ? err.message
        : `${voiceMeta.name} could not reach the model. What's below was found from your words.`;
      setLlmError(msg);
    } finally {
      setLlmLoading(false);
    }
  }, [llmConfig, voice, harryVoiceMode, wesVoiceMode, voiceMeta, workspaceId, userId, record, title, overview, onPage, canonAware, buildPacketForQuery, target]);

  // Opened from ⌘K with a sentence: answer it at once. Recorded once, either way.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    void recordAppEvent('navigator.opened', {
      appId: 'navigator',
      state: initialQuery ? 'palette' : 'panel',
      target,
    });
    if (initialQuery) void runAsk(initialQuery);
  }, [initialQuery, runAsk, target]);

  const submit = () => { void runAsk(input); };

  const rerun = (entry: ConciergeEntry) => {
    setInput(entry.query);
    void runAsk(entry.query);
  };

  const askFor = (entry: CatalogEntry) => {
    setInput(entry.title);
    void runAsk(entry.title);
    setPickedId(entry.id);
  };

  // Recent: pinned questions first, then the latest, one row per question.
  const recent = useMemo(() => {
    const pinned = new Set(starred.map((s) => s.query));
    return [
      ...starred.map((s) => ({ entry: s, pinned: true })),
      ...history.filter((h) => !pinned.has(h.query)).map((h) => ({ entry: h, pinned: false })),
    ].slice(0, RECENT_SHOWN);
  }, [starred, history]);

  const showCornerSwitch = isAiCapabilityEnabled('co_facilitator', workspaceId);
  const canChangeVoice = can('admin.settings');

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16 backdrop-blur-sm animate-fadeIn"
      onMouseDown={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden border border-[var(--rule)]/80 bg-[var(--bg-raised)] shadow-2xl dark:border-[var(--rule-bright)] dark:bg-[var(--surface)]"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Navigator"
      >
        <div className="flex items-center gap-3 border-b border-[var(--rule)] px-5 py-4">
          <AssistantAvatar mode={voice} size={34} />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-[var(--text-pri)]">Navigator</h3>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
              Say what you want to do. It finds the place to do it, or where to start.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto self-start p-1.5 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="relative">
            <input
              autoFocus
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              placeholder='e.g. "add 15 items", "pick between four vendors", "find duplicates"'
              aria-label="What do you want to do?"
              className="w-full border border-[var(--rule)] py-2.5 pl-3 pr-10 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:border-[var(--rule-bright)] dark:bg-[var(--bg)] dark:placeholder:text-[var(--text-ter)] dark:focus:ring-0"
            />
            <button
              type="button"
              onClick={submit}
              disabled={!input.trim() || llmLoading}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-[var(--text-ter)] hover:bg-[var(--slate-dim)] disabled:hover:bg-transparent dark:text-[#8EA7D8] dark:hover:bg-[var(--active-tint)] dark:disabled:text-[var(--text-dim)]"
              aria-label="Ask the Navigator"
            >
              {llmLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CornerDownLeft className="h-4 w-4" />}
            </button>
          </div>

          {voice === 'harry' && (
            <button
              type="button"
              onClick={() => { void spin(); }}
              disabled={!harryConfig?.enabled || spinning}
              className={`${rowButton} disabled:opacity-50`}
              title="Spin a Harry-ism"
              aria-label="Spin the wheel for a Harry-ism"
            >
              <span className={`inline-flex h-8 w-8 flex-shrink-0 items-center justify-center border ${
                spinning
                  ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[rgba(61,75,111,0.45)] dark:bg-[var(--active-tint)] dark:text-[#8EA7D8]'
                  : 'border-transparent text-[var(--text-ter)]'
              }`}>
                <HarryWheelIcon className={`h-4 w-4 ${spinning ? 'animate-spin' : ''}`} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-[var(--text-pri)]">Spin the wheel</span>
                <span className="block text-[11px] text-[var(--text-ter)]">
                  {spinning ? 'Drawing a Harry-ism…' : 'Get a quote tuned to this page'}
                </span>
              </span>
            </button>
          )}

          {voice === 'harry' && (ism || ismError || spinning) && (
            <HarryIsmCard
              ism={ism}
              error={ismError}
              spinning={spinning}
              saved={ism ? isIsmFavorite(ism) : false}
              onSave={ism ? () => toggleIsmFavorite(userId, { text: ism, pageTitle: title }) : undefined}
              onDismiss={clearIsm}
            />
          )}

          {submitted && voiceMeta.usesLlm && (llmReply || llmLoading || llmError) && (
            <div className="border border-[var(--slate)] bg-[var(--slate-dim)]/40 p-3 dark:border-[var(--rule-bright)] dark:bg-[var(--bg-raised)]">
              <div className="mb-1.5 flex items-center gap-2">
                <AssistantAvatar mode={voice} size={22} />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                  {voiceMeta.name}
                </span>
              </div>
              {llmLoading && <p className="text-sm text-[var(--text-sec)]">Thinking…</p>}
              {llmError && !llmLoading && (
                <p className="text-xs text-[var(--mustard)] dark:text-[var(--coral)]">{llmError}</p>
              )}
              {llmReply && !llmLoading && (
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-pri)]">{llmReply}</p>
              )}
            </div>
          )}

          {submitted && (
            matches.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs text-[var(--text-ter)]">Here's where to do that:</p>
                {matches.slice(0, 3).map((m, i) => (
                  m.entry.kind === 'job' ? (
                    <JobCard
                      key={m.entry.id}
                      job={m.entry}
                      query={submitted}
                      showHowTo={i === 0}
                      onGo={(href) => go(href, m.entry.id)}
                    />
                  ) : (
                    <StartCard
                      key={m.entry.id}
                      start={m.entry}
                      query={submitted}
                      onStart={() => start(m.entry as StartEntry)}
                    />
                  )
                ))}
              </div>
            ) : !llmLoading && (
              <div className="border border-dashed border-[var(--rule)] p-4 text-center dark:border-[var(--rule-bright)]">
                <p className="text-sm text-[var(--text-sec)]">I don't know where that is done yet.</p>
                <p className="mt-1 text-xs text-[var(--text-ter)]">Try other words, or pick from everything below.</p>
              </div>
            )
          )}

          {!submitted && needsYou.length > 0 && (
            <div>
              <p className={sectionLabel}>Needs you now</p>
              <div className="space-y-2">
                {needsYou.slice(0, NEEDS_YOU_SHOWN).map((card) => {
                  const href = getResponsibilityNavigationHref(card);
                  if (!href) return null;
                  return (
                    <button
                      key={`${card.id}:${card.subject_type ?? ''}`}
                      type="button"
                      onClick={() => go(href, 'needs-you')}
                      className={rowButton}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-medium text-[var(--text-pri)]">{card.subject}</span>
                        {card.why && <span className="block truncate text-[11px] text-[var(--text-ter)]">{card.why}</span>}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => go('/prioritization/my-work', 'my-work')}
                className="mt-2 text-[11px] font-medium text-[var(--text-sec)] underline-offset-2 hover:underline"
              >
                {needsYou.length > NEEDS_YOU_SHOWN
                  ? `All ${needsYou.length} in My work →`
                  : 'My work →'}
              </button>
            </div>
          )}

          {!submitted && onPage.length > 0 && (
            <div>
              <p className={sectionLabel}>What you can do on {title}</p>
              <div className="space-y-2">
                {onPage.map((c) => (
                  <button key={c.id} type="button" onClick={() => askFor(c)} className={rowButton}>
                    <c.icon className="h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs font-medium text-[var(--text-pri)]">{c.title}</span>
                      <span className="block truncate text-[11px] text-[var(--text-ter)]">{c.summary}</span>
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {!submitted && recent.length > 0 && (
            <div>
              <div className="flex items-baseline">
                <p className={sectionLabel}>Recent</p>
                {history.length > 0 && (
                  <button
                    type="button"
                    onClick={() => clearHistory(userId)}
                    className="ml-auto inline-flex items-center gap-1 text-[11px] text-[var(--text-ter)] hover:text-[var(--coral)]"
                  >
                    <Trash2 className="h-3 w-3" /> Clear
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {recent.map(({ entry, pinned }) => (
                  <RecentRow
                    key={`${pinned ? 'p' : 'h'}:${entry.query}`}
                    entry={entry}
                    pinned={pinned}
                    onRerun={() => rerun(entry)}
                    onPin={() => toggleStar(userId, entry)}
                  />
                ))}
              </div>
            </div>
          )}

          {!submitted && voice === 'harry' && favoriteIsms.length > 0 && (
            <div>
              <p className={sectionLabel}>Saved Harry-isms</p>
              <div className="space-y-2">
                {favoriteIsms.map((entry) => (
                  <HarryIsmRow key={entry.id} entry={entry} onRemove={() => removeIsmFavorite(userId, entry.id)} />
                ))}
              </div>
            </div>
          )}

          {!submitted && (
            <div>
              <p className={sectionLabel}>Everything it can help with</p>
              <div className="flex flex-wrap gap-2">
                {CATALOG.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => askFor(c)}
                    className="rounded-full border border-[var(--rule)] px-3 py-1 text-xs text-[var(--text-sec)] transition-colors hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] hover:text-[var(--text-ter)] dark:border-[var(--rule-bright)] dark:hover:border-[rgba(61,75,111,0.45)] dark:hover:bg-[var(--active-tint)] dark:hover:text-[var(--text-pri)]"
                  >
                    {c.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-[var(--rule)] px-5 py-2.5 text-[11px] text-[var(--text-ter)]">
          <span>{voiceMeta.usesLlm ? `Answers in ${voiceMeta.voiceName}.` : 'Finds the place by your words, with no AI.'}</span>
          {canChangeVoice && (
            <button
              type="button"
              onClick={() => go('/prioritization/settings/agents', 'voice-settings')}
              className="font-medium underline-offset-2 hover:text-[var(--text-sec)] hover:underline"
            >
              Change the voice
            </button>
          )}
          {showCornerSwitch && (
            <button
              type="button"
              onClick={() => {
                setCornerAssistant('co_facilitator');
                onClose();
              }}
              className="ml-auto font-medium underline-offset-2 hover:text-[var(--text-sec)] hover:underline"
            >
              Put the Co-Facilitator in the corner
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function HarryIsmRow({ entry, onRemove }: { entry: HarryIsmEntry; onRemove: () => void }) {
  return (
    <div className="flex items-start gap-2 border border-[var(--rule)] px-3 py-2.5 dark:border-[var(--rule-bright)] dark:bg-[var(--bg-raised)]">
      <Quote className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--text-ter)]" />
      <span className="min-w-0 flex-1">
        <p className="text-sm italic leading-relaxed text-[var(--text-pri)]">“{entry.text}”</p>
        {entry.pageTitle && (
          <p className="mt-1 text-[11px] text-[var(--text-ter)]">Saved on {entry.pageTitle}</p>
        )}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="p-1 text-[var(--mustard)] transition-colors"
        title="Remove from saved"
        aria-label="Remove from saved"
      >
        <Star className="h-3.5 w-3.5 fill-current" />
      </button>
    </div>
  );
}

function RecentRow({
  entry, pinned, onRerun, onPin,
}: {
  entry: ConciergeEntry;
  pinned: boolean;
  onRerun: () => void;
  onPin: () => void;
}) {
  const found = entry.capabilityId ? getEntry(entry.capabilityId) : undefined;
  return (
    <div className="flex items-center gap-2 border border-[var(--rule)] px-3 py-2 dark:border-[var(--rule-bright)] dark:bg-[var(--bg-raised)]">
      <button type="button" onClick={onRerun} className="flex min-w-0 flex-1 items-center gap-2 text-left">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium text-[var(--text-pri)]">{entry.query}</span>
          {found && <span className="block text-[11px] text-[var(--text-ter)]">{found.title}</span>}
        </span>
      </button>
      <button
        type="button"
        onClick={onPin}
        className={`p-1 transition-colors ${
          pinned ? 'text-[var(--mustard)]' : 'text-[var(--text-ter)] hover:text-[var(--mustard)] dark:text-[var(--text-dim)]'
        }`}
        title={pinned ? 'Unpin' : 'Keep at the top'}
        aria-label={pinned ? 'Unpin' : 'Keep at the top'}
      >
        <Star className={`h-3.5 w-3.5 ${pinned ? 'fill-current' : ''}`} />
      </button>
    </div>
  );
}

/** @deprecated Use HarryPanel */
export const ConciergePanel = HarryPanel;
